import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Grid,
  TextField,
  Autocomplete,
  InputAdornment,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  Receipt as ReceiptIcon,
  Email as EmailIcon,
  Print as PrintIcon,
  Visibility as PreviewIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useNotify, useGetList } from 'react-admin';
import { useBrand } from './ServerBrandProvider';

interface ServiceBlueprint {
  id: string;
  name: string;
  description?: string;
  basePrice: number;
  estimatedDuration?: number;
  active: boolean;
  taxable: boolean;
  qbAccountRef?: string;
}

interface LineItem {
  id?: string;
  serviceBlueprintId?: string;
  serviceBlueprint?: ServiceBlueprint;
  name: string;
  description: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
  amount: number;
  estimatedDuration?: number;
  isCustom?: boolean;
}

interface Customer {
  id: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  email?: string;
}

interface EstimateData {
  id?: string;
  estimateNumber: string;
  customerId: string;
  customer?: Customer;
  title: string;
  issueDate: Date;
  validUntil: Date;
  lineItems: LineItem[];
  notes?: string;
  terms?: string;
  status: 'draft' | 'sent' | 'approved' | 'rejected' | 'expired';
}

interface EstimateWizardProps {
  onSave: (data: EstimateData, action: 'draft' | 'send') => Promise<void>;
  onComplete: (estimateId: string) => void;
  isLoading?: boolean;
  preselectedCustomerId?: string;
}

const steps = ['Basic Information & Services', 'Review & Additional Info', 'Preview & Actions'];

export const EstimateWizard: React.FC<EstimateWizardProps> = ({
  onSave,
  onComplete,
  isLoading = false,
  preselectedCustomerId,
}) => {
  const notify = useNotify();
  const { brand } = useBrand();
  const [activeStep, setActiveStep] = useState(0);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [savedEstimateId, setSavedEstimateId] = useState<string | null>(null);
  const [serviceSearchQuery, setServiceSearchQuery] = useState('');

  // Get tenant settings for tax rate and estimate numbering
  const {
    data: tenantSettings,
    isLoading: isLoadingTenantSettings,
    error: tenantSettingsError,
  } = useGetList('crm/tenant-settings', {
    pagination: { page: 1, perPage: 1 },
  });

  // Get customers for autocomplete - backend now sorts by displayName by default
  const { data: customers = [] } = useGetList<Customer>('customers', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'displayName', order: 'ASC' },
  });

  // Get service blueprints for line items
  const { data: serviceBlueprints = [] } = useGetList<ServiceBlueprint>('crm/service-blueprints', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
    filter: { active: true },
  });

  // Get tenant settings data
  const currentTenantSettings = tenantSettings?.[0];

  // Form state - estimate number will be auto-generated by server if not provided
  const [formData, setFormData] = useState<EstimateData>({
    estimateNumber: '', // Empty by default - server will generate
    customerId: '',
    customer: undefined,
    title: '',
    issueDate: new Date(),
    validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
    lineItems: [],
    notes: '',
    terms: 'Payment due within 30 days of estimate approval.',
    status: 'draft',
  });

  // Tax enabled state - user can only enable/disable, not change rate
  const [taxEnabled, setTaxEnabled] = useState(true);

  // Update tax enabled state when tenant settings load
  useEffect(() => {
    if (currentTenantSettings && !formData.id) {
      setTaxEnabled(currentTenantSettings.taxEnabled || true);
    }
  }, [currentTenantSettings, formData.id]);

  // Pre-populate customer when preselectedCustomerId is provided
  useEffect(() => {
    if (preselectedCustomerId && customers.length > 0 && !formData.customerId) {
      const preselectedCustomer = customers.find(customer => customer.id === preselectedCustomerId);
      if (preselectedCustomer) {
        setFormData(prev => ({
          ...prev,
          customerId: preselectedCustomerId,
          customer: preselectedCustomer,
        }));
      }
    }
  }, [preselectedCustomerId, customers, formData.customerId]);

  // Show error if tenant settings are required but not available
  if (tenantSettingsError || (!isLoadingTenantSettings && !currentTenantSettings)) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        <Alert severity="error">
          <Typography variant="h6" gutterBottom>
            Configuration Error
          </Typography>
          <Typography>
            Unable to load tenant settings. Please contact your administrator to configure tax rates
            and estimate numbering.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // Show loading state while tenant settings are loading
  if (isLoadingTenantSettings) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Typography>Loading configuration...</Typography>
      </Box>
    );
  }

  // Calculate totals
  const calculateTotals = () => {
    if (!formData.lineItems || formData.lineItems.length === 0) {
      return { subtotal: 0, taxAmount: 0, totalAmount: 0 };
    }

    const subtotal = formData.lineItems.reduce((sum, item) => {
      const qty = Number(item.quantity) || 0;
      const price = Number(item.unitPrice) || 0;
      return sum + qty * price;
    }, 0);

    const taxRate = taxEnabled ? currentTenantSettings?.primaryTaxRate || 0 : 0;
    const taxAmount = subtotal * (taxRate / 100);
    const totalAmount = subtotal + taxAmount;

    return { subtotal, taxAmount, totalAmount, taxRate };
  };

  const { subtotal, taxAmount, totalAmount, taxRate } = calculateTotals();

  const handleNext = () => {
    if (activeStep === 0) {
      // Validate step 1
      if (!formData.customerId) {
        notify('Please select a customer', { type: 'warning' });
        return;
      }
      // Allow estimates without services, but if services are added, they must have names
      if (formData.lineItems.length > 0 && formData.lineItems.some(item => !item.name.trim())) {
        notify('All line items must have a name', { type: 'warning' });
        return;
      }
    }

    if (activeStep === 1) {
      // Save estimate as draft before moving to preview
      handleSave('draft');
      return;
    }

    setActiveStep(prevActiveStep => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleCustomerChange = (customer: Customer | null) => {
    if (customer) {
      setFormData(prev => ({
        ...prev,
        customerId: customer.id,
        customer,
        title: prev.title || `Estimate for ${customer.displayName}`,
      }));
    }
  };

  const addServiceFromBlueprint = (serviceBlueprint: ServiceBlueprint) => {
    const newLineItem: LineItem = {
      serviceBlueprintId: serviceBlueprint.id,
      serviceBlueprint,
      name: serviceBlueprint.name,
      description: serviceBlueprint.description || '',
      quantity: 1,
      unitPrice: serviceBlueprint.basePrice,
      taxRate: 0,
      amount: serviceBlueprint.basePrice,
      estimatedDuration: serviceBlueprint.estimatedDuration,
      isCustom: false,
    };

    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, newLineItem],
    }));
  };

  const removeLineItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index),
    }));
  };

  const handleLineItemChange = (index: number, field: keyof LineItem, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const handleSave = async (action: 'draft' | 'send') => {
    try {
      const {
        subtotal: calcSubtotal,
        taxAmount: calcTaxAmount,
        totalAmount: calcTotalAmount,
        taxRate: calcTaxRate,
      } = calculateTotals();

      const dataToSave = {
        ...formData,
        subtotal: calcSubtotal,
        taxAmount: calcTaxAmount,
        totalAmount: calcTotalAmount,
        taxRate: calcTaxRate,
        taxEnabled: taxEnabled,
        status: action === 'send' ? ('sent' as const) : ('draft' as const),
      };

      console.log('EstimateWizard: Attempting to save estimate:', dataToSave);
      const result = await onSave(dataToSave, action);
      console.log('EstimateWizard: Save successful:', result);
      console.log('EstimateWizard: Result type:', typeof result);
      console.log('EstimateWizard: Result keys:', result ? Object.keys(result) : 'null');
      console.log('EstimateWizard: Result.data:', result?.data);
      console.log('EstimateWizard: Result.id:', result?.id);
      console.log('EstimateWizard: Result.data?.id:', result?.data?.id);

      // Handle both wrapped and unwrapped responses
      // useCreate returns data directly, but onSave might wrap it
      const estimateData = result?.data || result;
      console.log('EstimateWizard: Estimate data:', estimateData);
      console.log('EstimateWizard: Estimate data id:', estimateData?.id);

      // Validate that we got a valid result with an ID
      if (!estimateData || !estimateData.id) {
        console.error('EstimateWizard: Validation failed - result:', result, 'estimateData:', estimateData);
        throw new Error('Failed to create estimate: No ID returned from server');
      }

      if (action === 'draft') {
        // Move to next step after saving
        setSavedEstimateId(estimateData.id);
        setActiveStep(2);
        notify('Estimate saved as draft', { type: 'success' });
      } else {
        notify('Estimate sent successfully!', { type: 'success' });
        onComplete(estimateData.id);
      }
    } catch (error: any) {
      console.error('EstimateWizard: Error saving estimate:', error);
      const errorMessage =
        error?.message || error?.body?.message || 'Failed to save estimate. Please try again.';
      notify(`Error saving estimate: ${errorMessage}`, { type: 'error' });
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const renderStep1 = () => (
    <Grid container spacing={3}>
      {/* Basic Information */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <BusinessIcon color="primary" />
              Basic Information
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Autocomplete
                  options={customers}
                  getOptionLabel={option => option.displayName || ''}
                  value={formData.customer || null}
                  onChange={(_, value) => handleCustomerChange(value)}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Customer"
                      required
                      placeholder="Search for a customer..."
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="body1">{option.displayName}</Typography>
                        {option.email && (
                          <Typography variant="body2" color="text.secondary">
                            {option.email}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Estimate Number"
                  value={formData.estimateNumber}
                  onChange={e => setFormData(prev => ({ ...prev, estimateNumber: e.target.value }))}
                  placeholder="Leave empty to auto-generate"
                  helperText="Leave empty and the system will generate a unique estimate number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">#</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Website Development Project"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Issue Date"
                  value={formData.issueDate}
                  onChange={date => date && setFormData(prev => ({ ...prev, issueDate: date }))}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarIcon />
                          </InputAdornment>
                        ),
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Valid Until"
                  value={formData.validUntil}
                  onChange={date => date && setFormData(prev => ({ ...prev, validUntil: date }))}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarIcon />
                          </InputAdornment>
                        ),
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Services */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MoneyIcon color="primary" />
                Services
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setServiceDialogOpen(true)}
                size="small"
              >
                Add Service
              </Button>
            </Stack>

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Service</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: 80 }}>Qty</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Unit Price</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Amount</TableCell>
                    <TableCell sx={{ width: 50 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.lineItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No services added yet. Click &quot;Add Service&quot; to get started.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    formData.lineItems.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <TextField
                            fullWidth
                            variant="standard"
                            value={item.name}
                            onChange={e => handleLineItemChange(index, 'name', e.target.value)}
                            placeholder="Service name"
                            sx={{ mb: 1 }}
                          />
                          <TextField
                            fullWidth
                            variant="standard"
                            value={item.description}
                            onChange={e =>
                              handleLineItemChange(index, 'description', e.target.value)
                            }
                            placeholder="Description (optional)"
                            multiline
                            maxRows={2}
                            InputProps={{
                              style: { fontSize: '0.875rem', color: 'text.secondary' },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            variant="standard"
                            type="number"
                            value={item.quantity}
                            onChange={e =>
                              handleLineItemChange(index, 'quantity', Number(e.target.value))
                            }
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            variant="standard"
                            type="number"
                            value={item.unitPrice}
                            onChange={e =>
                              handleLineItemChange(index, 'unitPrice', Number(e.target.value))
                            }
                            inputProps={{ min: 0, step: 0.01 }}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(item.quantity * item.unitPrice)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={() => removeLineItem(index)}
                            size="small"
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Totals Preview */}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Box sx={{ minWidth: 200 }}>
                <Stack spacing={1}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography>Subtotal:</Typography>
                    <Typography>{formatCurrency(subtotal)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography>
                      Tax ({currentTenantSettings?.primaryTaxName || 'Tax'} {taxRate}%):
                    </Typography>
                    <Typography>{formatCurrency(taxAmount)}</Typography>
                  </Box>
                  <Divider />
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Total:
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {formatCurrency(totalAmount)}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Service Selection Dialog */}
      <Dialog
        open={serviceDialogOpen}
        onClose={() => {
          setServiceDialogOpen(false);
          setServiceSearchQuery(''); // Reset search when closing
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Service to Estimate</DialogTitle>
        <DialogContent
          sx={{
            minHeight: 400,
            maxHeight: 600,
            overflowY: 'auto',
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select a service from your service blueprints to add to this estimate.
          </Typography>

          {/* Search Input */}
          <TextField
            fullWidth
            placeholder="Search services by name or description..."
            value={serviceSearchQuery}
            onChange={e => setServiceSearchQuery(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          {/* Filtered Services */}
          {(() => {
            const filteredServices = serviceBlueprints.filter(service => {
              if (!serviceSearchQuery.trim()) return true;
              const query = serviceSearchQuery.toLowerCase();
              return (
                service.name.toLowerCase().includes(query) ||
                (service.description && service.description.toLowerCase().includes(query))
              );
            });

            if (filteredServices.length === 0) {
              return (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {serviceSearchQuery.trim()
                      ? `No services found matching "${serviceSearchQuery}"`
                      : 'No active service blueprints found.'}
                  </Typography>
                  {!serviceSearchQuery.trim() && (
                    <Typography variant="caption" color="text.secondary">
                      Create service blueprints in Configuration to get started.
                    </Typography>
                  )}
                </Box>
              );
            }

            return (
              <Stack spacing={2}>
                {filteredServices.map(service => (
                  <Paper
                    key={service.id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' },
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                    onClick={() => {
                      addServiceFromBlueprint(service);
                      setServiceDialogOpen(false);
                      setServiceSearchQuery(''); // Reset search when adding
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box flex={1}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {service.name}
                        </Typography>
                        {service.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {service.description}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                          <Chip
                            label={`${formatCurrency(service.basePrice)}`}
                            color="primary"
                            size="small"
                          />
                        </Stack>
                      </Box>
                      <Button variant="outlined" size="small" startIcon={<AddIcon />}>
                        Add
                      </Button>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setServiceDialogOpen(false);
              setServiceSearchQuery(''); // Reset search when closing
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );

  const renderStep2 = () => (
    <Grid container spacing={3}>
      {/* Summary */}
      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Estimate Summary
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {formData.customer?.displayName || 'No customer selected'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formData.title}
              </Typography>
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Service</TableCell>
                    <TableCell>Qty</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.lineItems.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {item.name}
                          </Typography>
                          {item.description && (
                            <Typography variant="caption" color="text.secondary">
                              {item.description}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{formatCurrency(item.unitPrice)}</TableCell>
                      <TableCell>{formatCurrency(item.quantity * item.unitPrice)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* Additional Information */}
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Additional Information
            </Typography>

            <Stack spacing={3}>
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={taxEnabled}
                      onChange={e => setTaxEnabled(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={`Apply Tax (${currentTenantSettings?.primaryTaxName || 'Tax'} ${
                    currentTenantSettings?.primaryTaxRate || 0
                  }%)`}
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Tax rate is set by your organization and cannot be modified
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Internal notes (not visible to customer)..."
              />

              <TextField
                fullWidth
                label="Terms & Conditions"
                multiline
                rows={3}
                value={formData.terms}
                onChange={e => setFormData(prev => ({ ...prev, terms: e.target.value }))}
                placeholder="Payment terms, conditions..."
              />

              <Divider />

              <Stack spacing={1}>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Subtotal:</Typography>
                  <Typography>{formatCurrency(subtotal)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>
                    Tax ({currentTenantSettings?.primaryTaxName || 'Tax'} {taxRate}%):
                  </Typography>
                  <Typography>{formatCurrency(taxAmount)}</Typography>
                </Box>
                <Divider />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Total:
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {formatCurrency(totalAmount)}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderStep3 = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Alert severity="success" sx={{ mb: 3 }}>
          Estimate saved successfully! You can now preview, email, or print the estimate.
        </Alert>
      </Grid>

      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Estimate Created
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {formData.title}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Estimate #{formData.estimateNumber}
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                <strong>Customer:</strong> {formData.customer?.displayName}
              </Typography>
              <Typography variant="body1">
                <strong>Total:</strong> {formatCurrency(totalAmount)}
              </Typography>
            </Box>

            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<PreviewIcon />}
                onClick={() => setPreviewDialogOpen(true)}
              >
                Preview
              </Button>
              <Button
                variant="outlined"
                startIcon={<EmailIcon />}
                onClick={() => handleSave('send')}
                disabled={isLoading}
              >
                Email to Customer
              </Button>
              <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => window.print()}>
                Print
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Next Steps
            </Typography>

            <Stack spacing={2}>
              <Button
                variant="contained"
                fullWidth
                onClick={() => onComplete(savedEstimateId || 'new-estimate')}
              >
                View Estimate
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  // Reset wizard for new estimate
                  setActiveStep(0);
                  setFormData({
                    estimateNumber: '', // Empty - server will auto-generate unique number per tenant
                    customerId: '',
                    customer: undefined,
                    title: '',
                    issueDate: new Date(),
                    validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
                    lineItems: [],
                    notes: '',
                    terms: 'Payment due within 30 days of estimate approval.',
                    status: 'draft',
                  });
                  setTaxEnabled(currentTenantSettings?.taxEnabled || true);
                }}
              >
                Create Another Estimate
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Estimate Preview</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 3, border: '1px solid #ddd', borderRadius: 1 }}>
            <Typography variant="h5" gutterBottom>
              {formData.title}
            </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Estimate #{formData.estimateNumber}
            </Typography>

            <Grid container spacing={2} sx={{ my: 2 }}>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>To:</strong> {formData.customer?.displayName || 'Select Customer'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Valid Until:</strong> {formData.validUntil.toLocaleDateString()}
                </Typography>
              </Grid>
            </Grid>

            <TableContainer component={Paper} variant="outlined" sx={{ my: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell>Qty</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.lineItems.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {item.name}
                          </Typography>
                          {item.description && (
                            <Typography variant="caption" color="text.secondary">
                              {item.description}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{formatCurrency(item.unitPrice)}</TableCell>
                      <TableCell>{formatCurrency(item.quantity * item.unitPrice)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box display="flex" justifyContent="flex-end">
              <Stack spacing={1} sx={{ minWidth: 200 }}>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Subtotal:</Typography>
                  <Typography>{formatCurrency(subtotal)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>
                    Tax ({currentTenantSettings?.primaryTaxName || 'Tax'} {taxRate}%):
                  </Typography>
                  <Typography>{formatCurrency(taxAmount)}</Typography>
                </Box>
                <Divider />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="h6">Total:</Typography>
                  <Typography variant="h6">{formatCurrency(totalAmount)}</Typography>
                </Box>
              </Stack>
            </Box>

            {formData.terms && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Terms & Conditions:</strong>
                  <br />
                  {formData.terms}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background:
            brand?.theme?.headerGradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <CardContent>
          <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>
            Create Estimate
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ color: 'rgba(255,255,255,0.8)', textAlign: 'center' }}
          >
            {activeStep === 0 && 'Enter basic information and select services'}
            {activeStep === 1 && 'Review summary and add additional information'}
            {activeStep === 2 && 'Preview your estimate and choose next steps'}
          </Typography>
        </CardContent>
      </Card>

      {/* Stepper */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Step Content */}
      <Box sx={{ mb: 3 }}>
        {activeStep === 0 && renderStep1()}
        {activeStep === 1 && renderStep2()}
        {activeStep === 2 && renderStep3()}
      </Box>

      {/* Navigation */}
      {activeStep < 2 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button onClick={handleBack} disabled={activeStep === 0} startIcon={<BackIcon />}>
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={activeStep === 1 ? <ReceiptIcon /> : <NextIcon />}
                disabled={isLoading}
              >
                {activeStep === 1 ? 'Save & Continue' : 'Next'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
